import { useEffect, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Box, Typography } from '@mui/material';
import * as api from '../api/index';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { DropzoneFile, QuickbooksLogo, MicrosoftDynamics365, SageLogo } from '@lendica/components';
import ReceiptIcon from '@mui/icons-material/Receipt';
import { UnableToConnect } from './UnableToConnect';
import { UploadList } from './UploadList';
import SyncAltIcon from '@mui/icons-material/SyncAlt';
import ModeStandby from '@mui/icons-material/ModeStandby';

const services = [
    {
        logo: QuickbooksLogo,
        key: 'quickbooks',
    },
    {
        logo: MicrosoftDynamics365,
        key: 'microsoft',
    },
    {
        logo: SageLogo,
        key: 'sage',
    },
];

export const Accounting = ({ company_id, env }: { company_id: string; env: string }) => {
    const [expanded, setExpanded] = useState(false);
    const [uploadedFiles, setUploadedFiles] = useState<DropzoneFile[]>([]);
    const [connectedAccounts, setConnectedAccounts] = useState<string[]>([]);
    const [popupClosed, setPopupClosed] = useState(false);
    const [accountConnected, setAccountConnected] = useState(false);

    function launchPopup(service: string) {
        var win: any;
        var parameters = 'location=1,width=800,height=650';
        parameters += ',left=' + (screen.width - 800) / 2 + ',top=' + (screen.height - 650) / 2;

        setPopupClosed(false);
        // Launch Popup
        if (env === 'idev') {
            win = window.open(
                `https://micro-oauth-dev.herokuapp.com/oauth/${service}?company_id=${company_id}`,
                'connectPopup',
                parameters
            );
        } else if (env === 'sandbox') {
            win = window.open(
                `https://micro-oauth-sandbox.herokuapp.com/oauth/${service}?company_id=${company_id}`,
                'connectPopup',
                parameters
            );
        } else if (env === 'prod') {
            win = window.open(
                `https://oauth.app.golendica.com/oauth/${service}?company_id=${company_id}`,
                'connectPopup',
                parameters
            );
        }

        let interval = setInterval(() => {
            if (win.closed) {
                setPopupClosed(true);
                clearInterval(interval);
            }
        }, 1000);
    }

    const handleFileUpload = (file: any) => {
        setUploadedFiles(file);
    };

    useEffect(() => {
        const getConnections = async () => {
            const res = await api.getDataConnections();
            const connected = services
                .filter(service => res.some((x: any) => x.active && x.saas_name === service.key))
                .map(service => service.key);

            setConnectedAccounts(connected);
            setAccountConnected(!!connected.length);
        };
        getConnections();
    }, [popupClosed]);

    const handleClick = (service: string, isConnected: boolean) => {
        if (!isConnected) {
            launchPopup(service);
        }
    };

    return (
        <Accordion
            expanded={expanded}
            onChange={() => setExpanded(!expanded)}
            sx={{
                '&:before': {
                    display: 'none',
                },
                'borderRadius': 1,
                'boxShadow': 'none',
                'paddingTop': 1,
                'background': '##EDEDF4',
                'border': '1px solid #EDEDF4',
                'mt': 2,
                'mb': 2,
                'paddingBottom': expanded ? 0 : 1,
            }}
            data-testid="accounting-accordion"
        >
            <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                aria-controls="panel1a-content"
                id="panel1a-header"
                color="#FFF"
                sx={{
                    border: 'none',
                    boxShadow: 'none',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <ReceiptIcon />
                    <Typography fontWeight={500} ml={2}>
                        Accounting
                    </Typography>
                </Box>

                <Box
                    sx={{
                        display: 'flex',
                        justifyContent: 'flex-end',
                        paddingX: '1rem',
                        alignItems: 'center',
                        width: '100%',
                    }}
                >
                    {accountConnected && <CheckCircleIcon color="success" />}
                </Box>
            </AccordionSummary>

            <AccordionDetails
                sx={{
                    padding: '0',
                }}
            >
                <Box>
                    {services.map((service, index) => {
                        const Logo = service.logo;
                        const isConnected = connectedAccounts.includes(service.key);

                        return (
                            <Box
                                key={index}
                                sx={{
                                    boxSizing: 'border-box',
                                    display: 'flex',
                                    flexDirection: 'row',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    padding: '1rem 1rem',
                                    background: '#f2f2f8',
                                    borderRadius: 1,
                                    marginY: 1,
                                    marginX: 2,
                                    cursor: 'pointer',
                                    height: 78,
                                }}
                                component="div"
                                onClick={() => handleClick(service.key, isConnected)}
                                data-testid="open-accounting-service-popup"
                            >
                                <Logo />
                                {isConnected ? (
                                    <Box sx={{ display: 'flex' }}>
                                        <Typography mr={1}>connected</Typography>
                                        <ModeStandby color="success" />
                                    </Box>
                                ) : (
                                    <SyncAltIcon />
                                )}
                            </Box>
                        );
                    })}

                    <Box
                        sx={{
                            marginX: 2,
                            marginBottom: 1,
                        }}
                    >
                        <UnableToConnect
                            company_id={company_id}
                            caption={"I don't use any of the above accounting software."}
                            secondaryCaption={'Upload last years financial statements'}
                            uploadedFiles={uploadedFiles}
                            fileUploadHandler={handleFileUpload}
                            type={'accounting'}
                            env={env}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        height: 'fit-content',
                        display: 'flex',
                        flexDirection: 'column',
                        background: 'background.card',
                    }}
                >
                    <UploadList uploadedFiles={uploadedFiles} />
                </Box>
            </AccordionDetails>
        </Accordion>
    );
};
